import { Link } from 'react-router-dom';
import { Check, EyeOff, Camera, LayoutGrid, MessageSquare, Tag, Send } from 'lucide-react';
import SEOHead from '../SEOHead';
import RelatedLinks from './RelatedLinks';

const H2 = ({ children }) => <h2 className="text-xl font-display font-bold text-ink tracking-tight mb-4">{children}</h2>;

const Prose = ({ title, paragraphs }) => (
  <section>
    <H2>{title}</H2>
    <div className="space-y-3 text-[15px] font-display text-ink-secondary leading-relaxed">
      {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
    </div>
  </section>
);

const CAPABILITIES = [
  { icon: Tag, title: 'Pin notes to any element', desc: 'Click on headers, images, buttons, forms — any visible element on any page. Notes stay anchored when the layout shifts.' },
  { icon: Camera, title: 'Context captured automatically', desc: 'Every note records a screenshot, the page URL, browser, OS and viewport size. Screen recording on the Team plan.' },
  { icon: LayoutGrid, title: 'Triage in a dashboard', desc: 'Kanban and list views, filters by status, priority, assignee and type, bulk actions, CSV export.' },
  { icon: MessageSquare, title: 'Thread replies in place', desc: 'Discuss an issue right on the page where it lives. No more asking which page someone meant.' },
  { icon: Send, title: 'Push to Slack, Jira, Asana, Linear', desc: 'New notes notify Slack on Pro. One click turns a note into a ticket on Team.' },
  { icon: EyeOff, title: 'Invisible to visitors', desc: 'The layer renders only for authenticated team members and invited guests. Your audience sees your site, nothing else.' },
];

/**
 * Renders a /for/<platform> page from a data object. See VsShopify-style pages in for/*.jsx.
 */
export default function PlatformPage({ slug, name, title, description, h1, intro, requirement, steps, stepsNote, code, loads, where, extras = [], ctaTitle, ctaSub }) {
  return (
    <>
      <SEOHead title={title} description={description} path={`/for/${slug}`} />
      <div className="min-h-screen bg-cream">
        <header className="border-b border-ink-faint/20 bg-cream/80 backdrop-blur-xl">
          <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
            <Link to="/" className="text-base font-display font-semibold tracking-tight text-ink">Cayda</Link>
            <Link to="/sign-up" className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started free</Link>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-6 pt-16 pb-10">
          <p className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-4">Cayda for {name}</p>
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-ink tracking-tight leading-tight mb-5">{h1}</h1>
          <p className="text-base font-display text-ink-secondary leading-relaxed max-w-xl">{intro}</p>
        </div>

        <main className="max-w-3xl mx-auto px-6 pb-20">
          <div className="space-y-14">

            {/* Install */}
            <section>
              <H2>Install on {name}</H2>
              <div className="bg-white border border-ink-faint/20 rounded-2xl p-5 sm:p-6">
                {requirement && (
                  <p className="text-xs font-display text-ink-secondary bg-surface-sunken rounded-lg px-3 py-2 mb-5">{requirement}</p>
                )}
                <ol className="space-y-4">
                  {steps.map(({ title: t, desc }, i) => (
                    <li key={t} className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-accent/10 text-accent text-xs font-display font-semibold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                      <div>
                        <p className="text-[15px] font-display font-semibold text-ink">{t}</p>
                        {desc && <p className="text-sm font-display text-ink-secondary leading-relaxed mt-0.5">{desc}</p>}
                      </div>
                    </li>
                  ))}
                </ol>
                {code && (
                  <pre className="mt-5 bg-ink text-on-dark text-xs font-mono leading-relaxed rounded-xl p-4 overflow-x-auto"><code>{code}</code></pre>
                )}
                {stepsNote && <p className="mt-5 text-sm font-display text-ink-muted leading-relaxed">{stepsNote}</p>}
              </div>
            </section>

            <Prose title={loads.title} paragraphs={loads.paragraphs} />
            <Prose title={where.title} paragraphs={where.paragraphs} />
            {extras.map(e => <Prose key={e.title} title={e.title} paragraphs={e.paragraphs} />)}

            <section>
              <H2>What your team gets</H2>
              <div className="grid sm:grid-cols-2 gap-3">
                {CAPABILITIES.map(({ icon: Icon, title: t, desc }) => (
                  <div key={t} className="bg-white border border-ink-faint/20 rounded-2xl p-5">
                    <div className="w-9 h-9 rounded-xl bg-accent/10 flex items-center justify-center mb-3"><Icon className="w-4 h-4 text-accent" /></div>
                    <p className="text-[15px] font-display font-semibold text-ink mb-1.5">{t}</p>
                    <p className="text-sm font-display text-ink-secondary leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <H2>Pricing</H2>
              <div className="text-[15px] font-display text-ink-secondary leading-relaxed space-y-3">
                <p>Free for 2 websites, 50 notes per site and 3 team members, no credit card. Pro is $9 a month base plus $5 per member (billed annually) for unlimited sites, notes, file attachments, status, priority and assignment, an activity feed, CSV export and Slack. Team is $14 a month base plus $6 per member (billed annually) and adds screen recording, guest access for clients at no member fee, and Jira, Asana and Linear.</p>
                <ul className="grid sm:grid-cols-3 gap-2 text-sm">
                  {['Owner covered by the base fee', 'Members bill only once they accept', 'No page view caps or site limits on paid plans'].map(t => (
                    <li key={t} className="flex items-start gap-2 bg-white border border-ink-faint/20 rounded-xl px-3 py-2.5"><Check className="w-3.5 h-3.5 text-accent shrink-0 mt-1" /><span>{t}</span></li>
                  ))}
                </ul>
              </div>
            </section>

            <RelatedLinks current={`/for/${slug}`} />
          </div>

          <div className="mt-16 pt-12 border-t border-ink-faint/20 text-center">
            <h3 className="text-xl font-display font-bold text-ink tracking-tight mb-2">{ctaTitle}</h3>
            <p className="text-sm font-display text-ink-muted mb-5">{ctaSub}</p>
            <Link to="/sign-up" className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started free →</Link>
          </div>
        </main>

        <footer className="border-t border-ink-faint/20 bg-cream">
          <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between">
            <p className="text-xs font-display text-ink-faint">© {new Date().getFullYear()} Slowfast LLC</p>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Privacy</Link>
              <Link to="/terms" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Terms</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
